import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, LogOut, UserRound } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import api, { getFileUrl } from "@/lib/api";
import { LAYOUT } from "@/constants/testIds";
import Sidebar from "@/components/layout/Sidebar";
import { customRoleName } from "@/lib/customAccess";

const ROLE_LABELS = { admin: "Administradora", professional: "Profesional", reception: "Recepcion" };

export default function Header() {
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [clinicName, setClinicName] = useState("Strani Just Because");
  const [sheetOpen, setSheetOpen] = useState(false);

  useEffect(() => {
    api.get("/clinic").then(({ data }) => setClinicName(data.name)).catch(() => {});
  }, []);

  const handleLogout = async () => {
    await logout();
    toast({ title: "Sesión cerrada", description: "Hasta pronto." });
    navigate("/login");
  };

  const initials = (user?.name || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="h-16 border-b border-border bg-white/80 backdrop-blur-sm flex items-center justify-between px-4 sm:px-6 shrink-0">
      <div className="flex items-center gap-3">
        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button
              className="lg:hidden p-2 -ml-2 rounded-lg hover:bg-muted transition-colors"
              data-testid={LAYOUT.sidebarMobileToggle}
            >
              <Menu className="w-5 h-5 text-foreground" />
            </button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-64">
            <Sidebar onNavigate={() => setSheetOpen(false)} />
          </SheetContent>
        </Sheet>
        <span className="font-serif text-lg sm:text-xl text-foreground" data-testid={LAYOUT.headerClinicName}>
          {clinicName}
        </span>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2.5 rounded-lg px-2 py-1.5 hover:bg-muted transition-colors" data-testid={LAYOUT.headerUserMenuTrigger}>
            <Avatar className="w-8 h-8">
              <AvatarImage src={getFileUrl(user?.photo_url)} alt={user?.name} />
              <AvatarFallback className="bg-brand-light text-brand text-xs font-medium">{initials}</AvatarFallback>
            </Avatar>
            <div className="hidden sm:block text-left">
              <p className="text-sm font-medium text-foreground leading-tight">{user?.name}</p>
              <p className="text-xs text-muted-foreground leading-tight">{user?.role === "custom" ? customRoleName(user) : (ROLE_LABELS[user?.role] || user?.role)}</p>
            </div>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid={LAYOUT.headerProfileLink}>
            <UserRound className="w-4 h-4 mr-2" /> Mi Perfil
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={handleLogout} data-testid={LAYOUT.headerLogoutButton}>
            <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
